import { useState, useEffect, useRef } from 'react';
import { formatTime, getDirectionColor } from '../utils/helpers';
import { ArrowDownCircle, ArrowUpCircle, MapPin } from 'lucide-react';
import { attendanceAPI, createAuthenticatedWebSocket } from '../services/api';

const attendanceDayKey = (scanTime) => {
  const date = new Date(scanTime);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-CA');
};

const LiveFeed = () => {
  const [scans, setScans] = useState([]);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const shouldReconnectRef = useRef(true);

  useEffect(() => {
    attendanceAPI.getLogs({ limit: 20, today_only: true })
      .then((res) => setScans(res.data.logs || []))
      .catch(() => {});

    const connect = () => {
      const ws = createAuthenticatedWebSocket();
      wsRef.current = ws;

      ws.onopen = () => setConnected(true);
      ws.onclose = () => {
        setConnected(false);
        if (shouldReconnectRef.current) {
          reconnectTimeoutRef.current = setTimeout(connect, 3000);
        }
      };
      ws.onmessage = (event) => {
        const msg = JSON.parse(event.data);
        if (msg.type === 'NEW_SCAN') {
          setScans((prev) => {
            const incomingDay = attendanceDayKey(msg.data.scan_time);
            const next = [msg.data, ...prev.filter((scan) => (
              attendanceDayKey(scan.scan_time) === incomingDay
              && String(scan.user_id) !== String(msg.data.user_id)
            ))];
            return next.slice(0, 20);
          });
        }
      };
    };

    shouldReconnectRef.current = true;
    connect();

    return () => {
      shouldReconnectRef.current = false;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      wsRef.current?.close();
    };
  }, []);

  return (
    <div className="app-surface rounded-[1.8rem] overflow-hidden">
      <div className="flex items-center justify-between border-b border-slate-200/70 px-5 py-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Live pulse</p>
          <h2 className="mt-1 font-black text-slate-900">Scan feed</h2>
        </div>
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${connected ? 'bg-green-500 animate-pulse' : 'bg-red-400'}`}/>
          <span className="text-xs text-slate-500">{connected ? 'Live' : 'Reconnecting...'}</span>
        </div>
      </div>

      <div className="max-h-96 divide-y divide-slate-100/80 overflow-y-auto">
        {scans.length === 0 ? (
          <div className="p-8 text-center text-slate-400">
            <div className="text-4xl mb-2">📡</div>
            <p className="text-sm">Waiting for scans...</p>
          </div>
        ) : (
          scans.map((scan, i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-white/55">
              <div className={`rounded-full p-1 ${scan.direction === 'in' ? 'bg-emerald-50 text-emerald-600' : 'bg-orange-50 text-orange-600'}`}>
                {scan.direction === 'in'
                  ? <ArrowDownCircle size={20} />
                  : <ArrowUpCircle size={20} />
                }
              </div>
              <div className="flex-1 min-w-0">
                <p className="truncate text-sm font-semibold text-slate-900">{scan.full_name}</p>
                <p className="text-xs text-slate-400">{scan.tag_uid}</p>
                <p className="mt-1 text-xs text-slate-500">
                  {[scan.checkpoint_type === 'bus' ? 'Bus' : scan.checkpoint_type === 'main_gate' ? 'Main Gate' : null, scan.location_name || scan.reader_name || scan.reader_uid]
                    .filter(Boolean)
                    .join(' - ') || 'Unknown entry point'}
                </p>
                {scan.mobile_latitude != null && scan.mobile_longitude != null && (
                  <a
                    href={`https://www.google.com/maps?q=${scan.mobile_latitude},${scan.mobile_longitude}`}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:underline"
                  >
                    <MapPin size={12} />
                    GPS · {scan.mobile_accuracy_metres != null
                      ? `±${Math.round(Number(scan.mobile_accuracy_metres))} m`
                      : 'accuracy unavailable'}
                  </a>
                )}
              </div>
              <div className="text-right shrink-0">
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${getDirectionColor(scan.direction)}`}>
                  {scan.direction?.toUpperCase()}
                </span>
                <p className="mt-1 text-xs text-slate-400">{formatTime(scan.scan_time)}</p>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default LiveFeed;
